import { useLoading } from "@hooks";
import { useNotification } from "@utils/notifications";
import { useState } from "react";

const allowedTypes = [
	"image/jpeg",
	"image/png",
	"image/jpg",
	"application/pdf",
	"application/msword",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"application/vnd.ms-excel",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	"text/plain",
];

const maxSize = 5 * 1024 * 1024;

export default function useFileHandler(
	inputRef: React.RefObject<HTMLInputElement>,
	textInputRef?: React.RefObject<HTMLTextAreaElement>
) {
	const { setIsLoading } = useLoading();
	const [files, setFiles] = useState<File[]>([]);
	const { showError } = useNotification();

	function validateFiles(files: File[] | FileList): boolean {
		if (!allowedTypes.includes(files[0].type)) {
			showError("CHAT.SUPPORT.FILE_EXTENSION_GUIDELINE");

			return false;
		}

		if (files[0].size > maxSize) {
			showError("ACCOUNT_VERIFICATION.GUIDELINE_FILE_SIZE");
			return false;
		}
		return true;
	}

	function chooseFile(event: React.ChangeEvent<HTMLInputElement>) {
		event.persist();
		try {
			setIsLoading(true);
			if (event.target.files) {
				if (!validateFiles(event.target.files)) {
					return;
				}
				const files = Array.from(event.target.files);
				const merge: File[] = [...files];
				setFiles((i) => [...i, ...merge]);
				if (inputRef.current) {
					inputRef.current.value = "";
				}
				if (textInputRef && textInputRef.current) {
					textInputRef.current.focus();
				}
			}
		} catch (e) {
			console.error(e);
			showError(e.data.message);
		} finally {
			setIsLoading(false);
		}
	}

	function removeFile(id: number) {
		setFiles(files.filter((_, idx) => idx != id));
	}

	return {
		removeFile,
		chooseFile,
		files,
		setFiles,
	};
}
